import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";
import { CURSUS_HERO_IMAGE, getSlugIndex } from "@/lib/cursusMeta";

export default function CursusDetailHero({ cursus }) {
    const idx = getSlugIndex(cursus.slug);
    const img = CURSUS_HERO_IMAGE[cursus.slug];

    return (
        <section
            className="relative min-h-[96vh] w-full overflow-hidden bg-brand-ink text-white"
            data-testid="cursus-detail-hero"
        >
            {/* Ken Burns slow scale loop */}
            <motion.img
                src={img}
                alt={cursus.name}
                loading="eager"
                initial={{ scale: 1 }}
                animate={{ scale: 1.08 }}
                transition={{ duration: 20, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,10,10,0.55)_0%,rgba(10,10,10,0.75)_55%,rgba(10,10,10,0.98)_100%)]" />
            <div className="absolute inset-0 grain pointer-events-none opacity-40" />

            <div className="relative z-10 mx-auto flex min-h-[96vh] max-w-7xl flex-col justify-end px-6 pb-24 pt-40">
                {/* Breadcrumb */}
                <motion.nav
                    aria-label="Fil d'Ariane"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-2 self-start text-[11px] uppercase tracking-[0.26em] text-white/55"
                    data-testid="cursus-breadcrumb"
                >
                    <Link to="/" className="hover:text-white">Cursus Physio</Link>
                    <ChevronRight className="h-3 w-3" />
                    <Link to="/expertise-pa" className="hover:text-white">Cursus</Link>
                    <ChevronRight className="h-3 w-3" />
                    <span className="text-brand-orange">{cursus.name.replace("Cursus ", "")}</span>
                </motion.nav>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 inline-flex items-center gap-2 self-start rounded-full border border-white/20 bg-white/[0.06] px-4 py-1.5 text-[11px] uppercase tracking-[0.28em] backdrop-blur"
                >
                    <span className="font-display italic text-brand-orange">0{idx}</span>
                    <span>·</span>
                    <span>Cursus {idx} / 03</span>
                </motion.div>

                <h1
                    className="mt-8 max-w-5xl font-display font-normal leading-[0.88] tracking-tightest"
                    data-testid="cursus-detail-title"
                >
                    <motion.span
                        initial={{ opacity: 0, y: 32, filter: "blur(10px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="block text-[56px] sm:text-7xl lg:text-[128px]"
                    >
                        {cursus.name.replace("Cursus ", "")}
                    </motion.span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 max-w-3xl font-display italic text-brand-orange text-2xl md:text-3xl leading-tight"
                >
                    {cursus.prevention_angle || cursus.tagline}
                </motion.p>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg"
                >
                    {cursus.tagline}
                </motion.p>

                {/* Stats strip */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/15 pt-6 text-sm text-white/70"
                    data-testid="cursus-detail-stats"
                >
                    <span><strong className="text-white">3</strong> formations</span>
                    <span className="text-white/25">·</span>
                    <span><strong className="text-white">21h</strong> d'expertise</span>
                    <span className="text-white/25">·</span>
                    <span><strong className="text-white">1 170&nbsp;€</strong></span>
                    <span className="text-white/25">·</span>
                    <span className="text-brand-orange">Prise en charge FIF PL &amp; DPC</span>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
                >
                    <MagneticButton strength={0.22}>
                        <a
                            href="#parcours"
                            data-testid="hero-cta-composer"
                            className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition-all hover:bg-[#d9772c]"
                        >
                            Composer ce cursus
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                    </MagneticButton>
                    <MagneticButton strength={0.18}>
                        <a
                            href="#sessions"
                            data-testid="hero-cta-sessions"
                            className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-4 text-sm font-semibold text-white/90 transition-all hover:border-white/60 hover:text-white"
                        >
                            Voir les prochaines sessions
                            <ChevronRight className="h-4 w-4 rotate-90 transition-transform group-hover:translate-y-1" />
                        </a>
                    </MagneticButton>
                </motion.div>
            </div>
        </section>
    );
}
